import { useEffect, useRef } from 'react';
import { journeySteps } from '@/data/siteData';
import { scrollReveal, timelineProgress, prefersReducedMotion, gsap } from '@/hooks/useGsap';

const Journey = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sectionRef.current) {
      const titleEl = sectionRef.current.querySelector('.section-title');
      if (titleEl) scrollReveal(titleEl as HTMLElement);
    }

    const ctx = gsap.context(() => {
      if (timelineRef.current && progressRef.current) {
        timelineProgress(progressRef.current, timelineRef.current);

        // Animate timeline items
        if (!prefersReducedMotion()) {
          const items = timelineRef.current.querySelectorAll('.timeline-item');
          items.forEach((item, index) => {
            const circle = item.querySelector('.timeline-circle');
            const cardWrapper = item.querySelector('.journey-card-wrapper');

            const tl = gsap.timeline({
              scrollTrigger: {
                trigger: item,
                start: 'top 82%',
                toggleActions: 'play none none reverse',
                toggleClass: circle ? { targets: circle, className: 'active' } : undefined
              }
            });

            if (circle) {
              tl.fromTo(
                circle,
                { opacity: 0, scale: 0.6 },
                { 
                  opacity: 1,
                  scale: 1, 
                  duration: 0.45, 
                  ease: 'back.out(1.8)' 
                }
              );
            }

            if (cardWrapper) {
              tl.fromTo(
                cardWrapper,
                { 
                  x: index % 2 === 0 ? -45 : 45, 
                  opacity: 0,
                  scale: 0.94,
                  rotateY: index % 2 === 0 ? -5 : 5
                },
                { 
                  x: 0, 
                  opacity: 1,
                  scale: 1, 
                  rotateY: 0,
                  duration: 0.65, 
                  ease: 'power3.out' 
                },
                circle ? '-=0.3' : '0'
              );
            }
          });
        }
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="journey" ref={sectionRef} className="section-padding section-atmosphere bg-background border-t border-border">
      <div className="container-site max-w-3xl mx-auto">
        <div className="section-title text-center mb-16" style={{ opacity: prefersReducedMotion() ? 1 : 0 }}>
          <span className="chip mb-4">Journey</span>
          <h2 className="font-display uppercase font-light text-3xl sm:text-4xl tracking-[0.03em] text-foreground mb-4">
            Learning Journey
          </h2>
          <p className="text-muted-foreground text-xs uppercase tracking-wide leading-relaxed">
            Framework yang gue pakai buat approach automation projects.
          </p>
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="relative">
          {/* Progress Line */}
          <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-0.5 bg-border/20">
            <div
              ref={progressRef}
              className="absolute top-0 left-0 w-full bg-primary origin-top"
              style={{ height: '100%', transform: 'scaleY(0)' }}
            />
          </div>

          {/* Timeline Items */}
          <div className="space-y-12">
            {journeySteps && journeySteps.map((step, index) => (
              <div
                key={step.id}
                className={`timeline-item relative flex items-start gap-6 ${
                  index % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'
                }`}
              >
                {/* Number Circle */}
                <div 
                  className="timeline-circle absolute left-4 sm:left-1/2 -translate-x-1/2 w-8 h-8 rounded-none border border-border bg-card text-muted-foreground flex items-center justify-center font-bold text-xs z-10"
                  style={{ 
                    opacity: prefersReducedMotion() ? 1 : 0,
                    transform: prefersReducedMotion() ? 'translate(-50%, 0%) scale(1)' : 'translate(-50%, 0%) scale(0.6)'
                  }}
                >
                  {step.id}
                </div>

                {/* Content */}
                <div
                  className={`ml-16 sm:ml-0 sm:w-[calc(50%-2rem)] ${
                    index % 2 === 0 ? 'sm:pr-8 sm:text-right' : 'sm:pl-8'
                  }`}
                >
                  <div 
                    className="journey-card-wrapper"
                    style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
                  >
                    <div className="journey-card motion-panel bg-card rounded-none p-5 border border-border">
                      <h3 className="font-sans uppercase font-bold text-xs tracking-wider mb-2 text-foreground">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden sm:block sm:w-[calc(50%-2rem)]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Journey;
